import React, { useContext, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  PersonRounded,
  LightModeRounded,
  DarkModeRounded,
  AccountCircleRounded,
  LogoutRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { ThemeContext } from '../../context/ThemeContext';
import BrandMark from './BrandMark';

/**
 * StudentAppBar
 * Compact 64 px top bar for /student/* pages.
 * Always visible — shows logo on the left and profile avatar on the right.
 * Clean surface — no gradients, no aurora, no backdrop blur.
 *
 * @param {string}                    [title]
 * @param {string}                    [subtitle]
 * @param {string|number|function}    [backTo]  — number: navigate(n); string: navigate(s); function: call it
 * @param {ReactNode}                 [action]  — slot rendered between title and avatar; no extra wrapper
 * @param {boolean}                   [sticky=true]
 */
function StudentAppBar({ title, subtitle, backTo, action, sticky = true }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, logout } = useContext(AuthContext);
  const { mode, toggleTheme } = useContext(ThemeContext);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  const handleBack = () => {
    if (typeof backTo === 'function') {
      backTo();
    } else if (typeof backTo === 'string') {
      navigate(backTo);
    } else if (typeof backTo === 'number') {
      navigate(backTo);
    } else {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/student');
      }
    }
  };

  const handleOpenMenu = (e) => setMenuAnchor(e.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);

  const handleToggleTheme = () => {
    toggleTheme();
    handleCloseMenu();
  };

  const handleViewProfile = () => {
    handleCloseMenu();
    navigate('/student/profile');
  };

  const handleLogout = () => {
    handleCloseMenu();
    logout();
    navigate('/login');
  };

  const initial = user?.name?.charAt(0)?.toUpperCase() ?? null;
  const isDark = mode === 'dark';

  return (
    <Box
      component="header"
      sx={{
        height: 64,
        display: { xs: 'flex', md: 'none' },
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadowsExt?.sm,
        ...(sticky
          ? {
              position: 'sticky',
              top: 0,
              zIndex: theme.zIndex.appBar,
            }
          : {}),
      }}
    >
      {/* Logo — icon-only on mobile; desktop left-rail hides this bar entirely */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', mr: 0.5 }}>
        <BrandMark size={32} variant="compact" />
      </Box>

      {backTo != null && (
        <IconButton
          onClick={handleBack}
          size="small"
          aria-label="Go back"
          sx={{ color: 'text.primary', flexShrink: 0 }}
        >
          <ArrowBack />
        </IconButton>
      )}

      {/* Title / subtitle */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {title && (
          <Typography
            variant="h3"
            fontWeight={700}
            noWrap
            color="text.primary"
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Page-level action slot (e.g. edit button on Profile) */}
      {action && (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {action}
        </Box>
      )}

      {/* Profile avatar — opens dropdown menu */}
      <IconButton
        size="small"
        aria-label="Open account menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen ? 'true' : undefined}
        aria-controls={menuOpen ? 'student-account-menu' : undefined}
        onClick={handleOpenMenu}
        sx={{ flexShrink: 0, p: 0.5 }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'primary.soft',
            color: 'primary.main',
            fontWeight: 700,
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-hidden="true"
        >
          {initial ?? <PersonRounded sx={{ fontSize: 18 }} />}
        </Box>
      </IconButton>

      <Menu
        id="student-account-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 200,
              boxShadow: theme.shadowsExt?.md,
            },
          },
        }}
      >
        <MenuItem onClick={handleToggleTheme}>
          <ListItemIcon>
            {isDark ? (
              <LightModeRounded fontSize="small" />
            ) : (
              <DarkModeRounded fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>{isDark ? 'Light mode' : 'Dark mode'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewProfile}>
          <ListItemIcon>
            <AccountCircleRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>View profile</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'error.main' }}>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default StudentAppBar;
